import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, ChevronRight, ChevronLeft, CheckCircle2, Zap, Target, Shield, Gauge, Users, ShieldCheck, Brain, Dumbbell, Scale, Flame } from 'lucide-react';
import { getCategoriesForPosition, RATING_LABELS, getOverallScore, getCategoryScore } from '../data/criteria';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { PlayersAPI, EvaluationsAPI } from '../api';

const ICONS = { Zap, Target, Shield, Gauge, Users, ShieldCheck, Brain, Dumbbell, Scale, Flame };

export default function NewEvaluation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState(params.get('player') || '');
  const [gameContext, setGameContext] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [ratings, setRatings] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    PlayersAPI.list().then(list => {
      setPlayers(list);
      if (!playerId && list.length) setPlayerId(list[0].id);
    });
  }, []); // eslint-disable-line

  const player = players.find(p => p.id === playerId);

  // Position-aware rubric  swap in goalie categories automatically.
  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);

  // Reset the wizard back to step 0 if the rubric changes (different position).
  useEffect(() => { setStep(0); setRatings({}); }, [player?.position]);

  const currentCat = categories[step] || categories[0];
  const CatIcon = ICONS[currentCat.icon];

  const setRating = (catId, idx, val) => {
    setRatings(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [idx]: val } }));
  };

  const overall = useMemo(() => getOverallScore(ratings, categories), [ratings, categories]);
  const catScore = getCategoryScore(ratings, currentCat.id, categories);
  const isSelfAssessment = params.get('source') === 'self';

  const handleSave = async () => {
    if (!playerId) { toast({ title: 'Pick a player first' }); return; }
    setSaving(true);
    try {
      await EvaluationsAPI.create({
        playerId, evaluatorName: user?.name || 'Coach',
        gameContext: gameContext || (isSelfAssessment ? 'Self-Assessment' : 'Untitled Evaluation'),
        date, notes, ratings,
        source: isSelfAssessment ? 'self' : 'coach',
      });
      toast({ title: isSelfAssessment ? 'Self-assessment saved' : 'Evaluation saved', description: `${player?.firstName} ${player?.lastName}  Overall ${overall}` });
      navigate(`/app/players/${playerId}`);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.message || 'Try again.' });
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft className="h-4 w-4" /> Cancel
      </button>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Evaluation Details</div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Player</Label>
                <Select value={playerId} onValueChange={setPlayerId}>
                  <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-900">
                    {players.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}  #{p.jerseyNumber}  {p.position}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {player && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Rubric:</span>
                  <Badge className={player.position === 'Goalie' ? 'bg-violet-500/10 text-violet-700 border-violet-500/30' : 'bg-sky-500/10 text-sky-600 border-sky-500/30'}>
                    {player.position === 'Goalie' ? 'Goalie framework' : 'Skater framework'}
                  </Badge>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Game / Context</Label>
                <Input value={gameContext} onChange={e => setGameContext(e.target.value)}
                  placeholder="vs. Blue Ridge - Nov 4"
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Date</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                  className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
            </div>
            {overall > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-slate-500">Running Overall</span>
                <span className="font-mono font-bold text-2xl bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">{overall}</span>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3 px-1">Categories</div>
            <div className="space-y-1">
              {categories.map((c, i) => {
                const done = ratings[c.id] && Object.keys(ratings[c.id]).length >= c.items.length;
                const active = step === i;
                return (
                  <button key={c.id} onClick={() => setStep(i)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      active ? 'bg-sky-500/10 text-sky-600 border border-sky-500/20' : 'text-slate-500 hover:bg-slate-100 border border-transparent'
                    }`}>
                    <span className="font-mono text-xs w-4 text-slate-400">{i + 1}</span>
                    <span className="flex-1 text-left truncate">{c.name}</span>
                    {done && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
                {CatIcon && <CatIcon className="h-5 w-5 text-sky-600" />}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Step {step + 1} of {categories.length}</div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{currentCat.name}</h2>
              </div>
            </div>
            {catScore > 0 && (
              <div className="text-right">
                <div className="font-mono font-bold text-xl text-sky-600">{catScore}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Category</div>
              </div>
            )}
          </div>

          <div className="space-y-5">
            {currentCat.items.map((item, idx) => {
              const val = ratings[currentCat.id]?.[idx] || 0;
              return (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="text-sm text-slate-700 flex-1">{item}</div>
                    <div className="text-right min-w-[60px]">
                      <div className="font-mono font-bold text-lg text-slate-900">{val || ''}</div>
                      <div className="text-[10px] text-slate-500">{val ? RATING_LABELS[val] : 'Not rated'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-10 gap-1">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <button key={n} onClick={() => setRating(currentCat.id, idx, n)}
                        className={`h-9 rounded-md text-sm font-mono font-semibold transition-all ${
                          val === n ? 'bg-sky-500 text-slate-900 scale-105'
                            : val >= n ? 'bg-sky-500/25 text-sky-200'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-600'
                        }`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {step === categories.length - 1 && (
            <div className="mt-6">
              <Label className="text-sm text-slate-600">Coach's Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                placeholder="Overall impressions, focus areas for next practice, key moments from this game..."
                className="mt-1.5 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400" />
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 bg-transparent">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            {step < categories.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : 'Save Evaluation'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
